import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  Euro,
  Layers3,
  Calendar,
  Link,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
} from "lucide-react";

import React from "react";
import { EmptyList } from "../../../components";
import { formatDate } from "../../../helpers/date";

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor("date", {
    header: () => (
      <span className="flex items-center">
        <Calendar className="mr-2" size={16} /> Date
      </span>
    ),
    cell: (info) => info.getValue(),
  }),

  columnHelper.accessor("price", {
    id: "price",
    cell: (info) => (
      <span className="italic text-center text-blue-600">
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className="flex items-center">
        <Euro className="mr-2" size={16} /> Prix
      </span>
    ),
  }),
  columnHelper.accessor("priceVariation", {
    header: () => (
      <span className="flex items-center">
        <Layers3 className="mr-2" size={16} /> Prix
      </span>
    ),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor("quantity", {
    id: "quantity",
    cell: (info) => (
      <span className="italic text-center text-blue-600">
        {info.getValue()}
      </span>
    ),
    header: () => (
      <span className="flex items-center">
        <Euro className="mr-2" size={16} /> Stock
      </span>
    ),
  }),
  columnHelper.accessor("quantityVariation", {
    header: () => (
      <span className="flex items-center">
        <Layers3 className="mr-2" size={16} /> Qty
      </span>
    ),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor("ca", {
    header: () => (
      <span className="flex items-center">
        <Link className="mr-2" size={16} /> CA
      </span>
    ),
    cell: (info) => info.getValue(),
  }),
];

export const ProductScansTable = ({ data }) => {
  const [sorting, setSorting] = React.useState([]);
  const [globalFilter, setGlobalFilter] = React.useState("");

  const table = useReactTable({
    data: (data?.scans?.results || []).map((scan) => ({
      date: formatDate(scan.createdAt),
      price: scan.price,
      priceVariation: scan.priceVariation,
      quantity: scan.quantity,
      quantityVariation: scan.quantityVariation,
      ca: scan.ca,
    })),
    // @ts-ignore
    columns,
    enableColumnResizing: true,
    state: {
      sorting,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 5,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    // @ts-ignore
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),

    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (data?.scans?.results?.length === 0) {
    return <EmptyList text="Aucune règle" />;
  }

  return (
    <div className="flex flex-col mx-auto py-2 sm:px-6 lg:px-2">
      <div className="overflow-x-auto bg-white rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 tracking-wider"
                  >
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? "cursor-pointer select-none flex items-center justify-center"
                          : "",
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() ? (
                        <span>
                          {header.column.getIsSorted() === "desc" ? (
                            <ArrowDownWideNarrow size={14} className="ml-2" />
                          ) : (
                            <ArrowUpWideNarrow size={14} className="ml-2" />
                          )}
                        </span>
                      ) : (
                        <ArrowUpDown className="ml-2" size={14} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-2 py-1 whitespace-nowrap text-xs text-gray-500 text-center"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
